import React, { useEffect, useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

interface BottomSheetProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  /** Optional sticky footer (CTAs) that stays pinned while the body scrolls. */
  footer?: React.ReactNode
  className?: string
  /** When false, tapping the backdrop does not close the sheet (use the X / drag). */
  dismissOnBackdrop?: boolean
}

type SheetEntry = {
  id: number
  notifyStack: (offset: number) => void
}

let sheetIdSeq = 0
const sheetStack: SheetEntry[] = []

const applyStackOffsets = () => {
  for (let i = 0; i < sheetStack.length; i++) {
    sheetStack[i].notifyStack(sheetStack.length - 1 - i)
  }
}

const pushSheet = (entry: SheetEntry) => {
  if (sheetStack.length === 0) {
    document.body.style.overflow = 'hidden'
  }
  sheetStack.push(entry)
  applyStackOffsets()
}

const popSheet = (id: number) => {
  const idx = sheetStack.findIndex(s => s.id === id)
  if (idx === -1) {
    return
  }
  sheetStack.splice(idx, 1)
  if (sheetStack.length === 0) {
    document.body.style.overflow = ''
  }
  applyStackOffsets()
}

const ANIM_MS = 320

export const BottomSheet = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  className = '',
  dismissOnBackdrop = true,
}: BottomSheetProps) => {
  const [mounted, setMounted] = useState(isOpen)
  const [visible, setVisible] = useState(false)
  const [dragY, setDragY] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [stackOffset, setStackOffset] = useState(0)
  const startY = useRef(0)
  const sheetIdRef = useRef<number | null>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (isOpen) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
      setMounted(true)
      if (sheetIdRef.current === null) {
        const id = ++sheetIdSeq
        sheetIdRef.current = id
        pushSheet({ id, notifyStack: setStackOffset })
      }
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)))
    } else if (mounted) {
      setVisible(false)
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
      }
      closeTimerRef.current = setTimeout(() => {
        setMounted(false)
        setDragY(0)
        if (sheetIdRef.current !== null) {
          popSheet(sheetIdRef.current)
          sheetIdRef.current = null
        }
      }, ANIM_MS)
    }
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
      }
      if (sheetIdRef.current !== null) {
        popSheet(sheetIdRef.current)
        sheetIdRef.current = null
      }
    }
  }, [])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (dismissOnBackdrop && e.target === e.currentTarget) {
        handleClose()
      }
    },
    [handleClose, dismissOnBackdrop]
  )

  const handleHandleTouchStart = useCallback((e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY
    setDragging(true)
  }, [])

  const handleHandleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!dragging) {
        return
      }
      const delta = e.touches[0].clientY - startY.current
      if (delta > 0) {
        setDragY(delta)
      }
    },
    [dragging]
  )

  const handleHandleTouchEnd = useCallback(() => {
    setDragging(false)
    if (dragY > 100) {
      handleClose()
    } else {
      setDragY(0)
    }
  }, [dragY, handleClose])

  if (!mounted) {
    return null
  }

  const isTop = stackOffset === 0
  const baseZ = 50
  const activeIndex = sheetStack.findIndex(s => s.id === sheetIdRef.current)
  const zIndex = baseZ + (activeIndex === -1 ? 0 : activeIndex) * 10

  const pushBack = stackOffset > 0 ? Math.min(stackOffset, 3) * 6 : 0

  const sheetTransform = !visible ? 'translateY(100%)' : isTop ? `translateY(${dragY}px)` : `translateY(-${pushBack}px)`

  const backdropAlpha = !visible ? 0 : isTop ? 0.4 : 0.2

  return createPortal(
    <div
      className="fixed inset-0 flex items-end justify-center"
      style={{
        backgroundColor: `rgba(0,0,0,${backdropAlpha})`,
        transition: 'background-color 320ms ease',
        zIndex,
        pointerEvents: isTop ? 'auto' : 'none',
      }}
      onClick={handleBackdropClick}>
      <div
        className={`w-full max-h-[90vh] rounded-t-2xl flex flex-col ${className}`}
        style={{
          backgroundColor: 'var(--content-background-color, #fff)',
          transform: sheetTransform,
          transformOrigin: 'top center',
          transition: dragging ? 'none' : `transform ${ANIM_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`,
          pointerEvents: 'auto',
          boxShadow: isTop ? '0 -8px 32px rgba(0,0,0,0.18)' : '0 -4px 14px rgba(0,0,0,0.08)',
        }}>
        <div
          className="rounded-t-2xl"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--theme-color) 6%, var(--content-background-color))',
          }}>
          <div
            className="flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none select-none"
            onTouchStart={handleHandleTouchStart}
            onTouchMove={handleHandleTouchMove}
            onTouchEnd={handleHandleTouchEnd}>
            <div
              className="w-10 h-1 rounded-full"
              style={{ backgroundColor: 'color-mix(in srgb, var(--theme-color) 25%, #d1d5db)' }}
            />
          </div>

          {title && (
            <div className="flex items-center justify-between px-5 pb-3">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text-color)' }}>
                {title}
              </h3>
              <button
                type="button"
                onClick={handleClose}
                className="modal-close-btn w-9 h-9 p-0 rounded-full transition-all active:scale-90 flex items-center justify-center"
                style={{ minWidth: 0, minHeight: 0, border: 'none' }}
                aria-label="Close">
                <X size={18} strokeWidth={2.25} />
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto pb-5">{children}</div>

        {footer && (
          <div
            className="shrink-0 px-5 pt-3 pb-[max(env(safe-area-inset-bottom),1rem)] flex justify-center"
            style={{
              borderTop: '1px solid var(--border-color, #e5e7eb)',
              backgroundColor: 'color-mix(in srgb, var(--theme-color) 6%, var(--content-background-color))',
            }}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

export default BottomSheet
